import { ToggleGroup, ToggleGroupItem } from "ui/ToggleGroup";
import DetailPanel from "ui/DetailPanel";
import { PrivacyLevel } from "util/userUtils";
import { cn } from "util/cn";
import AddButton from "ui/AddButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from 'ui/InputSelect';
import ActorPrivacy from "./ActorPrivacy";
import { ViewerRelation } from "util/constants";
interface AlignmentTileProps {
  statement: string;
  statementNumber: number;
  response: number;
  importance: number;
  note?: string;
  privacy: PrivacyLevel;
  currentUserResponse?: number;
  currentUserImportance?: number;
  currentUserViewerRelation: ViewerRelation;
  actorType: "user" | "team";
  onRespond?: (statementId: number) => void;
  hasResponded?: boolean;
  statements?: Array<{
    id: number;
    statement: string;
    hasResponse: boolean;
  }>;
  onStatementChange?: (statementId: number) => void;
  isPanelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
  showSummaryOption?: boolean;
}

const AlignmentTile: React.FC<AlignmentTileProps> = ({
  statement,
  statementNumber,
  response,
  importance,
  note,
  privacy,
  currentUserResponse,
  currentUserImportance,
  currentUserViewerRelation,
  actorType = "user",
  onRespond,
  hasResponded = true,
  statements = [],
  onStatementChange,
  isPanelOpen = false,
  onPanelOpenChange,
  showSummaryOption = false
}) => {

  const responseOptions = [
    { value: -2, label: "Strongly Disagree" },
    { value: -1, label: "Disagree" },
    { value: 0, label: "Neutral" },
    { value: 1, label: "Agree" },
    { value: 2, label: "Strongly Agree" },
  ];

  const importanceOptions = [
    { value: -2, label: "Very Unimportant" },
    { value: -1, label: "Unimportant" },
    { value: 0, label: "Neutral" },
    { value: 1, label: "Important" },
    { value: 2, label: "Very Important" },
  ];


  const handleStatementChange = (statementIndex: string) => {
    const id = parseInt(statementIndex);
    if (onStatementChange) {
      onStatementChange(id);
    }
  };

  const showStatementSelector = statements.length > 0 && onStatementChange;

  return (
    <div className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal p-1 rounded-lg space-y-2 h-full">
        {showStatementSelector ? (
          <div className="mb-2">
            <Select 
              value={statementNumber.toString()}
              onValueChange={handleStatementChange}
            >
              <SelectTrigger className="input-select text-xs px-2 py-1.5 text-left font-medium text-eoe-text dark:text-eoe-dark-text">
                <SelectValue placeholder="Select statement">
                  <span className="border rounded-full px-1 mr-1 border-eoe-text dark:border-eoe-dark-text">{statementNumber}</span>
                  {statement}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="z-[2000] sm:max-w-[400px] border border-eoe-border-main dark:border-eoe-dark-border-main">
                {showSummaryOption && (
                  <SelectItem 
                    value="summary" 
                    className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text"
                  >
                    Summary
                  </SelectItem>
                )}
                <SelectGroup>
                  <SelectLabel>Answered</SelectLabel>
                  {statements.filter(s => s.hasResponse).map(stmt => (
                    <SelectItem 
                      key={`answered-${stmt.id}`} 
                      value={stmt.id.toString()} 
                      className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                    >
                      {stmt.id}. {stmt.statement}
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Unanswered</SelectLabel>
                  {statements.filter(s => !s.hasResponse).map(stmt => (
                    <SelectItem 
                      key={`unanswered-${stmt.id}`} 
                      value={stmt.id.toString()} 
                      className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                    >
                      {stmt.id}. {stmt.statement}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text bg-eoe-icon dark:bg-eoe-dark-icon rounded-lg p-1">
                <span className="border rounded-full px-1 border-eoe-text dark:border-eoe-dark-text">{statementNumber}</span> {statement}
            </h3>
          </div>
        )}

        <div className="flex justify-between gap-1">
          <ActorPrivacy 
              privacy={privacy}
              settingKey={`about_alignment_${statementNumber}`}
              onPrivacyChange={() => {}}
              actorType={actorType}
              editMode={false}
              showDropdown={false}
              miniIcon={false}
              disabled={true}
              viewerRelation={currentUserViewerRelation}
              className="opacity-50"
          />
          {(currentUserResponse !== undefined || currentUserImportance !== undefined) && (
              <div className="flex justify-center items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-eoe-blue"></div>
                  <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                  Your response
                  </span>
              </div>
          )}
        </div>

      {hasResponded && (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">Alignment</p>
        </div>
        <ToggleGroup type="single" value={response.toString()} className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl">
          {responseOptions.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value.toString()}
              disabled={true}
              className={cn(
                "flex-1 text-[9px] sm:text-xxs md:text-xs font-light data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                response === option.value && "bg-eoe-entity-user dark:bg-eoe-entity-user text-white dark:text-eoe-dark-text",
                currentUserResponse === option.value && (response !== option.value ? " bg-eoe-blue dark:bg-eoe-blue text-white dark:text-eoe-dark-text" : "bg-eoe-entity-user dark:bg-eoe-entity-user border-[6px] sm:border-8 border-eoe-blue dark:border-eoe-blue")
              )}
              aria-label={option.label}
            >
              {option.value === -2 && "Strongly Disagree"}
              {option.value === -1 && "Disagree"}
              {option.value === 0 && "Neutral"}
              {option.value === 1 && "Agree"}
              {option.value === 2 && "Strongly Agree"}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      )}

      {hasResponded && (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">Importance</p>
        </div>
        <ToggleGroup type="single" value={importance.toString()} className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl">
          {importanceOptions.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value.toString()}
              disabled={true}
              className={cn(
                "flex-1 text-[9px] sm:text-xxs md:text-xs font-light data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                importance === option.value && "bg-eoe-entity-user dark:bg-eoe-entity-user text-white dark:text-eoe-dark-text",
                currentUserImportance === option.value && (importance !== option.value ? " bg-eoe-blue dark:bg-eoe-blue text-white dark:text-eoe-dark-text" : "bg-eoe-entity-user dark:bg-eoe-entity-user border-[6px] sm:border-8 border-eoe-blue dark:border-eoe-blue")
              )}
              aria-label={option.label}
            >
              {option.value === -2 && "Very Unimportant"}
              {option.value === -1 && "Unimportant"}
              {option.value === 0 && "Neutral"}
              {option.value === 1 && "Important"}
              {option.value === 2 && "Very Important"}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      )}

      {note && hasResponded && (
        <DetailPanel 
          title="Note" 
          className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-modal-hover"
          open={isPanelOpen}
          onOpenChange={onPanelOpenChange}
        >
          <p className="text-xs text-eoe-text dark:text-eoe-dark-text whitespace-pre-wrap">{note}</p>
        </DetailPanel>
      )}

      {!hasResponded && (
        <>
          <AddButton
            tooltipTitle="Respond to this statement in order to view their response"
            buttonText="Respond to view"
            className="z-10 rounded-3xl w-full min-h-[100px] h-[40%]"
            noPlus={true}
            onClick={() => {
              if (onRespond) {
                onRespond(statementNumber);
              }
            }}
          />
          <p className="text-center text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">You can keep your response private, if you want</p>
        </>
      )}
    </div>
  );
};

export default AlignmentTile;
